import { SkeletonRows } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import { 
  fetchchunkLatestClients, 
  controlidfetchLogs 
} from '../../features/clients/clientsSlice';
import { getDeviceTimezoneAPI, stateTimezones } from '../../features/clients/clientsAPI';
import './management.css';

const Management = () => {
  const dispatch = useDispatch();
  const { fullclients, loading: loadingClients, logs, logsLoading } = useSelector((state) => state.clients);
  const { user } = useSelector((state) => state.auth);

  // --- STATE ---
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');

  const [selectedClient, setSelectedClient] = useState(null);
  const [deviceTzInfo, setDeviceTzInfo] = useState({ tz: 'America/Los_Angeles', ischange: 0 });
  const [events, setEvents] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  
  // Log Pagination State
  const [eventCurrentPage, setEventCurrentPage] = useState(1);
  const [eventRowsPerPage, setEventRowsPerPage] = useState(10);
  
  // Dialog Controls
  const [openingEvents, setOpeningEvents] = useState(false);
  const [isEventsDialogOpen, setIsEventsDialogOpen] = useState(false);
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  
  // Image State
  const [imageBytes, setImageBytes] = useState('');
  const [loadingImage, setLoadingImage] = useState(false);

  // Form State
  const [showBAC, setShowBAC] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [form, setForm] = useState({
    id: "",
    devid: "",
    controlId: "",
    rtime: "",
    eventName: "",
    typeofevent: "",
    bacvalue: "",
    result: "",
    passfailstatus: "255",
    enginerunstate: "255",
    randominitial: "255"
  });

  const eventOptions = [
    "INITIAL TEST",
    "RANDOM TEST",
    "CALIBRATION DONE",
    "SERVICE_PERIOD_SET",
    "Emergency_SOS"
  ];

  const normalizeEventName = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  const isImageEvent = (event) => {
    const eventName = normalizeEventName(event.event || event.typeofevent);
    return ['INITIAL_TEST', 'RANDOM_TEST', 'PICTURE_CAPTURED'].includes(eventName);
  };

  // --- LIFECYCLE ---
  useEffect(() => {
    dispatch(fetchchunkLatestClients());
  }, [dispatch]);

  useEffect(() => {
    if (logs && isEventsDialogOpen) {
      setEvents(logs);
    }
  }, [logs, isEventsDialogOpen]);

  // --- CLIENTS TABLE LOGIC ---
  const filteredClients = useMemo(() => {
    if (!fullclients) return [];
    return fullclients.filter(client => {
      const search = searchTerm.toLowerCase();
      return (
        String(client.id || '').toLowerCase().includes(search) ||
        String(client.client || '').toLowerCase().includes(search) ||
        String(client.cbid || '').toLowerCase().includes(search) ||
        String(client.devId || '').toLowerCase().includes(search) ||
        String(client.controlId || '').toLowerCase().includes(search)
      );
    });
  }, [fullclients, searchTerm]);

  const totalPages = Math.ceil(filteredClients.length / rowsPerPage);
  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const currentClients = filteredClients.slice(indexOfFirst, indexOfFirst + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
    }
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  };

  // --- EVENTS LOGIC ---
  const openClientEvents = async (client) => {
    setOpeningEvents(true);
    setEvents([]);
    setSelectedClient(client);
    setIsEventsDialogOpen(true);
    setEventSearch("");
    setEventCurrentPage(1);
    
    // Fetch Timezone for GMT conversion
    setDeviceTzInfo({ tz: 'America/Los_Angeles', ischange: 0 });
    if (client.sno) {
      try {
        const deviceInfo = await getDeviceTimezoneAPI(client.sno);
        if (deviceInfo) {
          if (deviceInfo.timezone === "GMT") {
            setDeviceTzInfo({ tz: stateTimezones[deviceInfo.tagstate] || "GMT", ischange: 1 });
          } else if (deviceInfo.timezone === "") {
            setDeviceTzInfo({ tz: "America/Los_Angeles", ischange: 0 });
          } else {
            setDeviceTzInfo({ tz: deviceInfo.timezone, ischange: 0 });
          }
        }
      } catch (err) {
        console.warn("Error fetching device timezone:", err);
      }
    }
    
    const obj = {
      item: client,
      role: user?.role || "superadmin",
      sorted: true
    };
    
    try { await dispatch(controlidfetchLogs(obj)); }
    finally { setOpeningEvents(false); }
  };

  const closeEventsDialog = () => {
    setIsEventsDialogOpen(false);
    setSelectedClient(null);
    setEvents([]);
  };

  // --- FORM LOGIC ---
  const getCurrentTime = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
  };

  const openAddDialog = () => {
    const controlId = selectedClient.cbid || selectedClient.controlId;
    setForm({
      id: "",
      devid: selectedClient.devId || "",
      controlId: controlId,
      rtime: getCurrentTime(),
      eventName: "",
      typeofevent: "",
      bacvalue: "",
      result: "",
      passfailstatus: "255",
      enginerunstate: "255",
      randominitial: "255"
    });
    setShowBAC(false);
    setShowResult(false);
    setIsEventFormOpen(true);
  };

  const onEventChange = (eventName) => {
    let updates = {
      eventName,
      bacvalue: "",
      passfailstatus: "255",
      enginerunstate: "255",
      randominitial: "255"
    };

    let showB = false;
    let showR = false;

    if (eventName === "INITIAL TEST") {
      updates.typeofevent = "254";
      updates.randominitial = "0";
      showB = true;
      showR = true;
    } else if (eventName === "RANDOM TEST") {
      updates.typeofevent = "254";
      updates.randominitial = "1";
      showB = true;
      showR = true;
    } else if (eventName === "CALIBRATION DONE") {
      updates.typeofevent = "22";
    } else if (eventName === "SERVICE_PERIOD_SET") {
      updates.typeofevent = "6";
    } else if (eventName === "Emergency_SOS") {
      updates.typeofevent = "100";
    }

    setShowBAC(showB);
    setShowResult(showR);
    setForm(prev => ({ ...prev, ...updates }));
  };

  const editEvent = (item) => {
    let eventName = "";
    let showB = false;
    let showR = false;
    let typeofevent = "";
    let randominitial = "255";

    if (item.event === "INITIAL TEST" || item.typeofevent === "INITIAL TEST") {
      eventName = "INITIAL TEST";
      showB = true;
      showR = true;
      typeofevent = "254";
      randominitial = "0";
    } else if (item.event === "RANDOM TEST" || item.typeofevent === "RANDOM TEST") {
      eventName = "RANDOM TEST";
      showB = true;
      showR = true;
      typeofevent = "254";
      randominitial = "1";
    } else if (item.event === "CALIBRATION DONE" || item.typeofevent === "CALIBRATION DONE") {
      eventName = "CALIBRATION DONE";
      typeofevent = "22";
    } else if (item.event === "SERVICE_PERIOD_SET" || item.typeofevent === "SERVICE_PERIOD_SET") {
      eventName = "SERVICE_PERIOD_SET";
      typeofevent = "6";
    } else if (item.event === "Emergency_SOS" || item.typeofevent === "Emergency_SOS") {
      eventName = "Emergency_SOS";
      typeofevent = "100";
    }

    setShowBAC(showB);
    setShowResult(showR);
    
    setForm({
      id: item.id,
      devid: item.devid || item.samplerId,
      controlId: item.controlId,
      rtime: item.time || item.rtime,
      eventName: eventName,
      typeofevent: typeofevent,
      bacvalue: item.bac || item.bacvalue || "",
      result: item.result || item.passfailstatus || "",
      passfailstatus: (item.passfailstatus === "PASS" || item.result === "PASS") ? "1" : ((item.passfailstatus === "FAIL" || item.result === "FAIL") ? "0" : "255"),
      enginerunstate: item.enginerunstate || "255",
      randominitial: randominitial
    });
    
    setIsEventFormOpen(true);
  };

  const saveEvent = async () => {
    let passfail = "255";
    if (form.result === "PASS") passfail = "1";
    else if (form.result === "FAIL") passfail = "0";

    let bac = "255";
    if (form.bacvalue !== "" && form.bacvalue !== null) {
      bac = Math.round(parseFloat(form.bacvalue) * 1000).toString();
    }

    let finalRtime = form.rtime;
    // Convert to GMT if backend expects GMT (ischange === 1) and not A300I
    if (selectedClient?.devicemode !== "A300I" && deviceTzInfo.ischange === 1) {
      try {
        finalRtime = moment.tz(form.rtime, "MM/DD/YYYY HH:mm:ss", deviceTzInfo.tz).utc().format("MM/DD/YYYY HH:mm:ss");
      } catch (err) {
        console.error("GMT Conversion Error:", err);
      }
    }

    const payload = {
      devid: form.devid,
      controlId: form.controlId,
      rtime: finalRtime,
      typeofevent: form.typeofevent,
      bacvalue: bac,
      passfailstatus: passfail,
      enginerunstate: form.enginerunstate,
      randominitial: form.randominitial
    };

    try {
      if (form.id) {
        await axios.post(`https://alcobrakeadmin.com/v3/v2/eeprom/updateinterlockevents/${form.id}`, payload);
        Swal.fire({ icon: 'success', title: 'Updated!', text: 'Event updated successfully', timer: 1500, showConfirmButton: false });
      } else {
        await axios.post("https://alcobrakeadmin.com/v3/v2/eeprom/addinterlockevents", payload);
        Swal.fire({ icon: 'success', title: 'Added!', text: 'Event added successfully', timer: 1500, showConfirmButton: false });
      }
      setIsEventFormOpen(false);
      // Refresh logs
      const obj = { item: selectedClient, role: user?.role || "superadmin", sorted: true };
      dispatch(controlidfetchLogs(obj));
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Operation failed' });
    }
  };

  const deleteEvent = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This event will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(`https://alcobrakeadmin.com/v3/v2/eeprom/deleteinterlockevents/${item.id}`);
      Swal.fire({ title: "Deleted!", text: "Event deleted successfully.", icon: "success", timer: 1500, showConfirmButton: false });
      // Refresh local list and logs
      const obj = { item: selectedClient, role: user?.role || "superadmin", sorted: true };
      dispatch(controlidfetchLogs(obj));
    } catch (error) {
      console.error(error);
      Swal.fire({ title: "Error!", text: "Delete failed", icon: "error" });
    }
  };

  // --- IMAGE LOGIC ---
  const viewImage = (data) => {
    setIsImageDialogOpen(true);
    setImageBytes("");
    setLoadingImage(true);

    const url = `https://alcobrakeadmin.com/v3/app/getImage/getb64v42?deviceId=${data.controlId}&Image=${encodeURIComponent(data.rawRtime || data.rtime)}`;

    axios.get(url, { responseType: "blob" })
      .then((res) => {
        const contentType = res.headers["content-type"] || "";
        if (!contentType.includes("image")) {
          const reader = new FileReader();
          reader.readAsText(res.data);
          reader.onloadend = () => {
            const text = reader.result;
            if (!text || text.length < 50) setImageBytes("");
            else setImageBytes(`data:image/jpeg;base64,${text}`);
          };
          return;
        }
        const blob = new Blob([res.data], { type: contentType });
        if (blob.size < 2000) {
          setImageBytes("");
          return;
        }
        setImageBytes(URL.createObjectURL(blob));
      })
      .catch((err) => {
        console.error("Image Fetch Error:", err);
        setImageBytes("");
      })
      .finally(() => setLoadingImage(false));
  };

  // --- LOGS TABLE LOGIC ---
  const filteredEventsAll = useMemo(() => {
    if (!events) return [];
    return events.filter(e => {
      const s = eventSearch.toLowerCase();
      return (
        String(e.id || '').toLowerCase().includes(s) ||
        String(e.event || e.typeofevent || '').toLowerCase().includes(s) ||
        String(e.time || e.rtime || '').toLowerCase().includes(s) ||
        String(e.bac || e.bacvalue || '').toLowerCase().includes(s) ||
        String(e.result || e.passfailstatus || '').toLowerCase().includes(s)
      );
    });
  }, [events, eventSearch]);

  const totalEventPages = Math.ceil(filteredEventsAll.length / eventRowsPerPage);
  const indexOfFirstEvent = (eventCurrentPage - 1) * eventRowsPerPage;
  const currentEvents = filteredEventsAll.slice(indexOfFirstEvent, indexOfFirstEvent + eventRowsPerPage);

  const handleEventPageChange = (page) => {
    if (page >= 1 && page <= totalEventPages) {
      setEventCurrentPage(page);
    }
  };

  const getPageNumbers = (current, total) => {
    const pages = [];
    const maxVisiblePages = 5;
    let start = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    let end = Math.min(total, start + maxVisiblePages - 1);
    if (end - start + 1 < maxVisiblePages) start = Math.max(1, end - maxVisiblePages + 1);
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) {
      if (end < total - 1) pages.push('...');
      pages.push(total);
    }
    return pages;
  };

  return (
    <div className="clients-app">
      <div className="clients-card">
        {/* Header */}
        <div className="clients-header">
          <div>
            <h1 className="clients-title">Clients Management</h1>
            <p className="clients-subtitle">Monitor and manage interlock device events</p>
          </div>
        </div>

        {/* Filters */}
        <div className="clients-filters">
          <div className="search-wrapper">
            <svg className="search-svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by Client ID, Name, CBID, or Device ID..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <div className="records-info">
            <span className="records-label">Show</span>
            <select 
              className="records-select"
              value={rowsPerPage} 
              onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="records-label">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="clients-table-container">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Name</th>
                <th>CBID</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingClients ? (
                <SkeletonRows columns={4} />
              ) : currentClients.length > 0 ? (
                currentClients.map((client, idx) => (
                  <tr key={`${client.id || 'client'}-${idx}`}>
                    <td className="client-id-cell">{client.id}</td>
                    <td className="client-name-cell">
                      <div className="client-avatar">
                        {(client.client || client.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span>{client.client || client.name}</span>
                    </td>
                    <td className="cbid-cell">{client.cbid}</td>
                    <td className="actions-cell text-center">
                      <button 
                        className="open-client-btn"
                        onClick={() => openClientEvents(client)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-8">No clients found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredClients.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {indexOfFirst + 1} to {Math.min(indexOfFirst + rowsPerPage, filteredClients.length)} of {filteredClients.length} entries
            </div>
            <div className="pagination-controls">
              <button className="page-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
              <button className="page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
              {getPageNumbers(currentPage, totalPages).map((p, i) => (
                p === '...' 
                  ? <span key={`dots-c-${i}`} className="page-dots">…</span>
                  : <button key={`page-c-${p}`} className={`page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => handlePageChange(p)}>{p}</button>
              ))}
              <button className="page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              <button className="page-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
            </div>
            <div className="goto-wrapper">
              <span>Go to</span>
              <form onSubmit={handleGoToPage}>
                <input type="number" min="1" max={totalPages} value={goToPage} onChange={e => setGoToPage(e.target.value)} placeholder="page" />
                <button type="submit">Go</button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* --- EVENTS LIST DIALOG --- */}
      {isEventsDialogOpen && (
        <div className="dialog-overlay" onClick={closeEventsDialog}>
          <div className="dialog-container wide" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">Events - {selectedClient?.client}</h2>
              <div className="dialog-actions">
                <div className="event-search-wrapper">
                   <input 
                    type="text" 
                    className="event-search-input" 
                    placeholder="Search events..." 
                    value={eventSearch}
                    onChange={(e) => { setEventSearch(e.target.value); setEventCurrentPage(1); }}
                  />
                </div>
                <button className="add-event-btn" onClick={openAddDialog}>
                  + Add Event
                </button>
                <button className="dialog-close" onClick={closeEventsDialog}>×</button>
              </div>
            </div>
            <div className="dialog-body">
              <div className="events-table-container">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Device ID</th>
                      <th>Event</th>
                      <th>Timestamp</th>
                      <th>BAC</th>
                      <th>Result</th>
                      <th className="text-center">Image</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openingEvents || logsLoading ? (
                      <SkeletonRows columns={8} />
                    ) : currentEvents.length > 0 ? (
                      currentEvents.map((event, idx) => (
                        <tr key={`${event.id || 'event'}-${idx}`}>
                          <td>{event.id}</td>
                          <td>{event.devid || event.samplerId}</td>
                          <td><span className={`event-badge ${(event.event || event.typeofevent)?.replace(/\s+/g, '-').toLowerCase()}`}>{event.event || event.typeofevent}</span></td>
                          <td>{event.time || event.rtime}</td>
                          <td>{event.bac || event.bacvalue}</td>
                          <td>
                            <span className={`status-badge ${(event.result || event.passfailstatus)?.toLowerCase()}`}>
                              {event.result || event.passfailstatus}
                            </span>
                          </td>
                          <td className="text-center">
                            {isImageEvent(event) && (
                              <button className="img-view-btn" onClick={() => viewImage(event)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                  <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                              </button>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="event-actions">
                              <button className="edit-icon-btn" onClick={() => editEvent(event)}>✎</button>
                              <button className="delete-icon-btn" onClick={() => deleteEvent(event)}>🗑</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="8" className="text-center py-8">No events found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Event Pagination */}
              {!openingEvents && !logsLoading && filteredEventsAll.length > 0 && (
                <div className="pagination-container event-pagination">
                  <div className="pagination-info">
                    Showing {indexOfFirstEvent + 1} to {Math.min(indexOfFirstEvent + eventRowsPerPage, filteredEventsAll.length)} of {filteredEventsAll.length}
                  </div>
                  <div className="pagination-controls">
                    <select 
                      className="rows-select small"
                      value={eventRowsPerPage} 
                      onChange={e => { setEventRowsPerPage(Number(e.target.value)); setEventCurrentPage(1); }}
                    >
                      {[10, 25, 50].map(n => <option key={`ev-row-${n}`} value={n}>{n}</option>)}
                    </select>
                    <button className="page-btn small" onClick={() => handleEventPageChange(1)} disabled={eventCurrentPage === 1}>«</button>
                    <button className="page-btn small" onClick={() => handleEventPageChange(eventCurrentPage - 1)} disabled={eventCurrentPage === 1}>‹</button>
                    {getPageNumbers(eventCurrentPage, totalEventPages).map((p, i) => (
                      p === '...' 
                        ? <span key={`dots-e-${i}`} className="page-dots">…</span>
                        : <button key={`page-e-${p}`} className={`page-btn small ${eventCurrentPage === p ? 'active' : ''}`} onClick={() => handleEventPageChange(p)}>{p}</button>
                    ))}
                    <button className="page-btn small" onClick={() => handleEventPageChange(eventCurrentPage + 1)} disabled={eventCurrentPage === totalEventPages}>›</button>
                    <button className="page-btn small" onClick={() => handleEventPageChange(totalEventPages)} disabled={eventCurrentPage === totalEventPages}>»</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- EVENT FORM DIALOG --- */}
      {isEventFormOpen && (
        <div className="dialog-overlay secondary" onClick={() => setIsEventFormOpen(false)}>
          <div className="dialog-container small" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">{form.id ? "Edit Event" : "Add Event"}</h2>
              <button className="dialog-close" onClick={() => setIsEventFormOpen(false)}>×</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>Control ID</label>
                <input type="text" value={form.controlId} readOnly className="form-input readonly" />
              </div>
              <div className="form-group">
                <label>Sampler / Device ID</label>
                <input type="text" value={form.devid} readOnly className="form-input readonly" />
              </div>
              <div className="form-group">
                <label>Timestamp</label>
                <input 
                  type="text" 
                  value={form.rtime} 
                  onChange={(e) => setForm({...form, rtime: e.target.value})}
                  className="form-input" 
                />
              </div>
              <div className="form-group">
                <label>Select Event</label>
                <select 
                  className="form-select" 
                  value={form.eventName} 
                  onChange={(e) => onEventChange(e.target.value)}
                >
                  <option value="">-- Select Event --</option>
                  {eventOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              {showBAC && (
                <div className="form-group">
                  <label>BAC</label>
                  <input 
                    type="text" 
                    value={form.bacvalue} 
                    onChange={(e) => setForm({...form, bacvalue: e.target.value})}
                    className="form-input" 
                    placeholder="e.g. 0.000"
                  />
                </div>
              )}
              {showResult && (
                <div className="form-group">
                  <label>Result</label>
                  <select 
                    className="form-select" 
                    value={form.result} 
                    onChange={(e) => setForm({...form, result: e.target.value})}
                  >
                    <option value="">-- Select Result --</option>
                    <option value="PASS">PASS</option>
                    <option value="FAIL">FAIL</option>
                  </select>
                </div>
              )}
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn cancel-btn" onClick={() => setIsEventFormOpen(false)}>Cancel</button>
              <button className="dialog-btn save-btn" onClick={saveEvent}>{form.id ? "Update" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* --- IMAGE VIEW DIALOG --- */}
      {isImageDialogOpen && (
        <div className="dialog-overlay tertiary" onClick={() => setIsImageDialogOpen(false)}>
          <div className="dialog-container image-preview" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">Event Image</h2>
              <button className="dialog-close" onClick={() => setIsImageDialogOpen(false)}>×</button>
            </div>
            <div className="dialog-body text-center">
              {loadingImage ? (
                <div className="image-loader">
                  <div className="spinner"></div>
                  <p>Loading image...</p>
                </div>
              ) : imageBytes ? (
                <img src={imageBytes} alt="Event" className="event-preview-img" />
              ) : (
                <div className="no-image">
                  <p>No Image Found</p>
                </div>
              )}
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn cancel-btn" onClick={() => setIsImageDialogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Management;
